import fetchAPI from '../../../utils/fetchApi';
import { getSessionStorageItem, setSessionStorageItem } from '../../../utils/sStorage';
import formatFeedItemsUrl from './utils/formatFeedItemsUrl';
import generateFeedItemsParams from './utils/generateFeedItemsParams';
import { trackGAEvent } from '../../../utils/gaTracking';
import { TFeed, TFeedItem } from 'homepage-web-bff/types/TFeeds';
import createFavorite from '../repositories/createFavorite';
import wasPageReloaded from '../../../utils/wasPageReloaded';

const HOMEPAGE_FEED = 'HOMEPAGE_FEED';

type TGetFeedItemsProps = {
  feed: TFeed;
  page: number;
  feedPostcode?: string;
  favoriteAdIds?: string[];
};

type TGetCurrentPageProps = {
  feed: TFeed;
  feedPostcode?: string;
};

const minutes = 60;
const seconds = 60;
const milliseconds = 1000;
const ttlInHours = 6;
// convert hours to milliseconds
const cacheTtl = ttlInHours * minutes * seconds * milliseconds;

const formatCacheKey = ({ feedType, postcode }): string =>
  postcode ? `${HOMEPAGE_FEED}_${feedType}_${postcode}` : `${HOMEPAGE_FEED}_${feedType}`;

const checkIfFavoriteItem = async (feedItems: TFeedItem[], xsrfToken) => {
  const favItem = getSessionStorageItem('FAVORITE_ITEM');
  if (favItem) {
    trackGAEvent('WatchlistAddBegin');
    const response = await createFavorite(favItem.itemId, xsrfToken);
    if (response && response.isFavorite) {
      trackGAEvent('WatchlistAddSuccess');
    }
    // remove from session storage
    sessionStorage.removeItem(`FAVORITE_ITEM_CREATED`);
    sessionStorage.removeItem(`FAVORITE_ITEM_VALUE`);
    const updatedFeedItems = feedItems.map((feed) => {
      return feed.itemId === favItem.itemId ? { ...feed, isFavorited: true } : feed;
    });
    return updatedFeedItems;
  }
  return feedItems;
};

export default async function getFeedItems(
  { feed, feedPostcode, page, favoriteAdIds }: TGetFeedItemsProps,
  userLoggedIn,
  xsrfToken,
): Promise<TFeedItem[]> {
  const params = generateFeedItemsParams({ feed, feedPostcode, page });

  const storageKey = formatCacheKey(params);
  // try to return cached items first
  // if the number of the cached page is greater or equal to the incoming then we return data for all cached pages
  const cachedData = getSessionStorageItem(storageKey, cacheTtl);
  if (cachedData?.feedItemsPage >= page) {
    if (wasPageReloaded() && userLoggedIn) {
      const favoriteIds = new Set(favoriteAdIds ?? []);
      const patchedItems = cachedData.feedItems.map((item: TFeedItem) => ({
        ...item,
        isFavorited: favoriteIds.has(item.itemId),
      }));
      setSessionStorageItem(storageKey, { ...cachedData, feedItems: patchedItems });
      if (xsrfToken && page === 0) {
        return checkIfFavoriteItem(patchedItems, xsrfToken);
      }
      return patchedItems;
    }
    if (userLoggedIn) {
      const updatedFeeds = await checkIfFavoriteItem(cachedData.feedItems, xsrfToken);
      return updatedFeeds;
    }
    return cachedData.feedItems;
  }

  try {
    let feedItems = await fetchAPI({ url: formatFeedItemsUrl(params), preloadCompatible: true });

    if (feedItems?.length) {
      if (userLoggedIn) {
        // check if there is any favorite item created in session storage
        feedItems = await checkIfFavoriteItem(feedItems, xsrfToken);
      }
      // cache response only if it contains feed items
      setSessionStorageItem(storageKey, {
        feedItemsPage: page,
        feedItems: [...(cachedData?.feedItems || []), ...feedItems],
      });
    }

    return feedItems;
  } catch {
    return [];
  }
}

export function getCurrentPage({ feed, feedPostcode }: TGetCurrentPageProps) {
  const storageKey = formatCacheKey(generateFeedItemsParams({ feed, feedPostcode }));

  const cachedData = getSessionStorageItem(storageKey, cacheTtl);

  return cachedData?.feedItemsPage ?? 0;
}
